import { Grapheme, Word } from '@/common/types/scopeAndSequence';
import {
  getDistinctGraphemes,
  getDistractorGraphemeWeight,
  weightedSample,
} from '@/common/utils/helpers';
import { getWordGraphemes } from '@/common/utils/scopeAndSequence';
import {
  playIntoAudio,
  playTurnTheWordAudio,
  playWordAudio,
  soundOutWord,
} from '../../common/utils/audio';

export const playPhonemeForgeGameInstructionsAudioBatch = async (
  baseWord: Word,
  wordToCreate: Word,
  segmentationEnabled: boolean,
  audioDelay: number,
  onSegmentationPlayed: (
    grapheme: Grapheme,
    index: number,
    audioDelay: number,
  ) => void,
  scene?: Phaser.Scene,
) => {
  await playTurnTheWordAudio();
  await playWordAudio(baseWord.spelling);
  await playIntoAudio();
  await playWordAudio(wordToCreate.spelling);
  if (segmentationEnabled) {
    const wordGraphemes = getWordGraphemes(wordToCreate);
    await soundOutWord(
      wordGraphemes,
      audioDelay,
      wordGraphemes,
      onSegmentationPlayed,
      scene,
    );
    await playWordAudio(wordToCreate.spelling);
  }
};

export function getInitialGraphemeOptions(
  baseWordGraphemes: Grapheme[],
  wordToCreateGraphemes: Grapheme[],
  availableGraphemes: Grapheme[],
  distractorCardsEnabled: boolean,
  numberOfDistractors: number = 4,
) {
  const countLetters = (graphemes: Grapheme[], id: string) =>
    graphemes.filter((g) => g.id === id).length;

  // Calculate the required letters to transform baseWord into wordToCreate
  const seen = new Set<string>();
  const requiredGraphemes: Grapheme[] = [];
  for (const grapheme of wordToCreateGraphemes) {
    // skip this grapheme if we've already processed it
    if (seen.has(grapheme.id)) continue;
    seen.add(grapheme.id);

    // figure out how many of this grapheme we need
    const numInBaseWord = countLetters(baseWordGraphemes, grapheme.id);
    const numInWordToCreate = countLetters(wordToCreateGraphemes, grapheme.id);
    const requiredCount = numInWordToCreate - numInBaseWord;

    // add that many of this grapheme to the required array
    if (requiredCount > 0) {
      requiredGraphemes.push(...Array(requiredCount).fill(grapheme));
    }
  }

  // Filter all word chain garphemes not included in the base word and that are not already in requiredLetters
  const additionalGraphemes = getDistinctGraphemes(
    [...baseWordGraphemes, ...wordToCreateGraphemes],
    availableGraphemes,
  );
  let distractorGraphemes: Grapheme[] = distractorCardsEnabled
    ? weightedSample(
        additionalGraphemes,
        (g) => getDistractorGraphemeWeight(g, wordToCreateGraphemes),
        numberOfDistractors,
      )
    : [];

  // Build the final set of options
  const options = [...requiredGraphemes, ...distractorGraphemes];

  // Shuffle the resulting letter options array for randomness before returning
  return options.sort(() => Math.random() - 0.5);
}
